import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnInit,
  Output,
  Provider,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, NG_VALUE_ACCESSOR, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { isEqual } from 'lodash';
import {
  AlertComponent,
  ButtonComponent,
  CardComponent,
  FormGroupComponent,
  FormsUtil,
  GroupButtonComponent,
  InputComponent,
  LoaderComponent,
  MbsSize,
  ModalService,
  ModalSettings,
  TagComponent,
  TreeAppendDirective,
  TreeComponent,
  TreeElement,
  TreeElementDirective
} from 'mbs-ui-kit';
import { CredentialsModalComponent } from 'mbs-ui-shared@components/credentials-modal/credentials-modal.component';
import { linuxInvalidPath, networkValidPath, windowsInvalidPath } from 'mbs-ui-shared@constants/folders-path-constants';
import { NetworkCredentials } from 'mbs-ui-shared@models/backup/network-credential';
import { ComputerContentRequestData, PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { linuxPathValidator, unixWithVariablesPathValidator, winNetworkPathValidator } from 'mbs-ui-shared@utils/validators';
import { BehaviorSubject, noop, Observable } from 'rxjs';
import { ViewMode } from 'wizards/constants/view-mode';
import { BaseForStepsHelper, DataForPath } from 'wizards/helpers/bases/base-for-steps-helper';
import { ExtendedTreeElement, GenerateTreeDataSettings, StepsHelpers } from 'wizards/helpers/steps-helpers';
import {
  AlternativeTreeIconPath,
  TreeIconPath,
  WhatBackupTreeStepFoldersForm,
  WhatBackupTreeStepValue,
  WhatBackupTreeStepValueForm
} from 'wizards/models/what-backup-tree-model';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { ParamsForTreeData, WizardStepsService } from 'wizards/services/wizard-steps.service';
import { CheckboxLegendComponent } from 'wizards/steps/components/checkbox-legend/checkbox-legend.component';
import { NetworkShareModalComponent } from 'wizards/steps/what-backup-tree-step/network-share-modal/network-share-modal.component';
import { AgentVersions, StepBase } from '../StepBase.class';

import { CommonModule } from '@angular/common';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { NgbDropdownModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { HelpLinkDirective } from 'mbs-ui-shared@help/helpLink.directive';

type MyTreeElement = TreeElement & { needUncheckIfChildUncheck?: boolean; notSetUncheckFlag?: boolean; isShare?: boolean; path?: string };
type PathArrays = { mainFormArray: string[]; excFormArray: string[]; newInc: string[]; newExc: string[] };

const ExistingFolderI18Key = 'shared:validation.existing_folder';

const WhatBackupTreeStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => WhatBackupTreeStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-what-backup-tree-step',
  templateUrl: './what-backup-tree-step.component.html',
  styleUrls: ['./what-backup-tree-step.component.scss'],
  providers: [WhatBackupTreeStepValueAccessor],
  imports: [
    AlertComponent,
    ButtonComponent,
    CardComponent,
    CommonModule,
    FormGroupComponent,
    FormsModule,
    InputComponent,
    LoaderComponent,
    NgbDropdownModule,
    NgbTooltipModule,
    ReactiveFormsModule,
    TreeAppendDirective,
    TreeComponent,
    I18NextPipe,
    HelpLinkDirective,
    TreeElementDirective,
    GroupButtonComponent,
    TagComponent
  ]
})
export class WhatBackupTreeStepComponent extends StepBase<WhatBackupTreeStepValue> implements OnInit {
  ability = inject(AbilityService);
  i18nPipe = inject(I18NextPipe);
  mainService: RemoteManagementWizardsService;
  private modal = inject(ModalService);
  private stepService = inject(WizardStepsService);

  @ViewChild(TreeComponent, { static: false }) treeComponent: TreeComponent;
  @ViewChild('includePathsInput', { read: ElementRef }) includePathsInput: ElementRef<HTMLElement>;
  @ViewChild('excludePathsInput', { read: ElementRef }) excludePathsInput: ElementRef<HTMLElement>;
  @Input() view?: ViewMode = ViewMode.Default;
  @Input() settings?: { hid: string; isUnix: boolean };
  @Output() switchToIBB = new EventEmitter<boolean>();

  public readonly size = MbsSize;
  public readonly viewMode = ViewMode;
  public readonly shareIconPath = AlternativeTreeIconPath[1];
  public readonly elementSelectors = {
    name: {
      defaultTitle: 'defaultTitle',
      onboardingTitle: 'onboardingTitle',
      isLoadingOrNoSectionAlert: 'isLoadingOrNoSectionAlert',
      treeElement: 'treeElement',
      plainTextFirstBlock: 'plainTextFirstBlock',
      plainTextSecondBlock: 'plainTextSecondBlock',
      advancedOptionsBtnShare: 'advancedOptionsBtnShare',
      IBBAlert: (item: Partial<TreeElement>) => `IBBAlert-${item.id}`,
      IBBAlertButton: (item: Partial<TreeElement>) => `IBBAlert-${item.id}-button`
    }
  };
  public readonly windowsPlaceholder = 'Example:\n' + 'C:\\Users\\Alex\\Documents\n' + 'D:\\Media\n' + '\\\\office-server\\shared\\Design';
  public readonly unixPlaceholder = 'Example:\n' + '/Users/alex/Documents\n' + '/Users/alex/Pictures\n' + '//office-server/shared/Design';
  private readonly networkCredentialsErrors: number[] = [1513, 1514];

  public minusPixels = 124;

  public isFileBackup = this.planType === PlanTypes.Plan;
  public showPlainText = false;
  public editedString: { value: string; isInclude: boolean; index: number } = null;
  public foldersForm: FormGroup<WhatBackupTreeStepFoldersForm>;
  public separator = ']-[';
  public treeData: Array<TreeElement & { canShowIBBAlert?: boolean; path?: string }> = [];

  public loading = false;
  public includePathsErrors: Record<number, ValidationErrors> = {};
  public excludePathsErrors: Record<number, ValidationErrors> = {};

  private needIncludeStrings: string[] = [];
  private needExcludeStrings: string[] = [];
  private needValidate = false;
  private needUpdate = false;

  private shareTreeItems: TreeElement[] = [];
  private networkSharePathArray: NetworkCredentials[] = [];

  private treeParams: ParamsForTreeData = {
    agentType: 'Backup',
    commandType: 'getComputerContent',
    params: { path: '', offset: 0, limit: 300, isAsync: true, order: 'DisplayNameAsc' }
  };

  get isUnix() {
    return super.isUnix || !!this.settings?.isUnix;
  }

  get hid(): string {
    return this.mainService.hid || this.settings?.hid;
  }

  get pastFromClipboardNotSupported(): boolean {
    return !navigator?.clipboard?.readText;
  }

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();
    this.mainService = mainService;

    this.setBaseFlags();
  }

  ngOnInit(): void {
    this.foldersForm = new FormGroup<WhatBackupTreeStepFoldersForm>({
      includeString: new FormControl('', [
        this.includeRequiredValidator.bind(this),
        this.pathValidator,
        this.isUnix ? this.isSelectedValidatorUnix.bind(this) : this.isSelectedValidatorWindows.bind(this)
      ]),
      excludeString: new FormControl('', [
        this.pathValidator,
        this.isUnix ? this.isSelectedValidatorUnix.bind(this) : this.isSelectedValidatorWindows.bind(this)
      ]),
      includePaths: new FormControl('', [this.pathsRequiredValidator.bind(this), this.pathsValidator(true)]),
      excludePaths: new FormControl('', [this.pathsValidator(false)])
    });

    this.initForm();

    this.setValues();
  }

  initForm(): void {
    this.stepForm = new FormGroup<WhatBackupTreeStepValueForm>({
      includeFolders: new FormControl([], [Validators.required]),
      excludeFolders: new FormControl([])
    });

    this.foldersForm.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      if (!value.includeString && !value.excludeString) this.needValidate = false;

      this.stepForm.patchValue({
        includeFolders: this.getFoldersFromText(value.includePaths),
        excludeFolders: this.getFoldersFromText(value.excludePaths)
      });

      this.revalidatePathsControls();
    });

    this.initFormEvents();
  }

  onStepFormChange(value: WhatBackupTreeStepValue): void {
    this.syncFoldersTextIfNeed(value);

    this.value = {
      ...value,
      valid: this.stepForm.valid && this.foldersForm.valid && !this.foldersForm.value.includeString && !this.foldersForm.value.excludeString
    };
  }

  forceValid(): void {
    const sec = this.foldersForm.value;
    this.needValidate = !!sec.includeString || !!sec.excludeString || !this.stepForm.value.includeFolders.length;
    FormsUtil.triggerValidation(this.foldersForm);
    FormsUtil.triggerValidation(this.stepForm);
  }

  updateForm(value: WhatBackupTreeStepValue): void {
    this.stepForm.reset(value);
    if (!this.showPlainText) this.getBaseTreeData();
  }

  setBaseFlags(): void {
    if (this.isRDMode || this.isOffline) this.showPlainText = true;
  }

  includeRequiredValidator(control: AbstractControl): ValidationErrors | null {
    if (!this.stepForm) return null;

    const incArray = this.stepForm.get('includeFolders').value;
    const errors = control.errors ? Object.keys(control.errors) : [];
    const valid = control.valid && (!errors.length || (errors.length === 1 && control.errors.required));
    return this.needValidate && ((valid && control.value) || !incArray.length)
      ? {
          required: {
            message: control.value
              ? this.i18nPipe.transform('wizards:includeTextNotAdded')
              : this.i18nPipe.transform('wizards:includeTextRequired')
          }
        }
      : null;
  }

  pathsRequiredValidator(control: AbstractControl): ValidationErrors | null {
    return this.needValidate && !this.getFoldersFromText(control.value).length
      ? { required: { message: this.i18nPipe.transform('wizards:includeTextRequired') } }
      : null;
  }

  pathsValidator(isInclude: boolean): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const errors = this.getPathsErrors(control, isInclude);

      isInclude ? (this.includePathsErrors = errors) : (this.excludePathsErrors = errors);

      return Object.keys(errors).length ? errors : null;
    };
  }

  getErrorsCount(isInclude = true): number {
    return Object.keys(isInclude ? this.includePathsErrors : this.excludePathsErrors).length;
  }

  getPathErrorMessage(errors: ValidationErrors): string {
    return Object.values(errors || {})[0]?.message || '';
  }

  focusPathLine(index: number, isInclude = true): void {
    const host = (isInclude ? this.includePathsInput : this.excludePathsInput)?.nativeElement;
    const textarea = host?.querySelector('textarea');

    if (!textarea) return;

    const lines = (textarea.value || '').split('\n');
    const lineIndex = Math.max(0, Math.min(index, lines.length - 1));
    const selectionStart = lines.slice(0, lineIndex).reduce((offset, line) => offset + line.length + 1, 0);
    const lineHeight = Number.parseFloat(getComputedStyle(textarea).lineHeight) || 18;

    textarea.focus();
    textarea.setSelectionRange(selectionStart, selectionStart);
    textarea.scrollTop = Math.max(0, lineIndex * lineHeight - textarea.clientHeight / 2 + lineHeight);
  }

  isSelectedValidatorUnix(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const error = { isSelected: { message: this.i18nPipe.transform(ExistingFolderI18Key) } };
    const incArray = this.stepForm.get('includeFolders').value;
    const excArray = this.stepForm.get('excludeFolders').value;
    const formValue = control.parent.getRawValue();
    const valid = formValue.includeString !== formValue.excludeString;

    if (this.editedString) return !valid || this.validateIsEditUnix(incArray, excArray, control.value) ? error : null;

    return !valid || ~incArray.indexOf(control.value) || ~excArray.indexOf(control.value) ? error : null;
  }

  isSelectedValidatorWindows(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const error = { isSelected: { message: this.i18nPipe.transform(ExistingFolderI18Key) } };
    const incArray = this.stepForm.get('includeFolders').value;
    const excArray = this.stepForm.get('excludeFolders').value;
    const formValue = control.parent.getRawValue();
    const valid = formValue.includeString.toLowerCase() !== formValue.excludeString.toLowerCase();
    const lowVal = control.value.toLowerCase();

    if (this.editedString) return !valid || this.validateIsEditWindows(incArray, excArray, lowVal) ? error : null;

    return !valid || ~incArray.findIndex((v) => v.toLowerCase() === lowVal) || ~excArray.findIndex((v) => v.toLowerCase() === lowVal)
      ? error
      : null;
  }

  validateIsEditUnix(incArray: string[], excArray: string[], value: string): boolean {
    const isNew = value !== this.editedString.value;

    if (this.editedString.isInclude) return !!(~incArray.indexOf(value) && isNew) || !!~excArray.indexOf(value);

    return !!(~excArray.indexOf(value) && isNew) || !!~incArray.indexOf(value);
  }

  validateIsEditWindows(incArray: string[], excArray: string[], value: string): boolean {
    const lowVal = value.toLowerCase();
    const isNew = lowVal !== this.editedString.value.toLowerCase();

    if (this.editedString.isInclude) {
      return (
        !!(~incArray.findIndex((v) => v.toLowerCase() === lowVal) && isNew) || !!~excArray.findIndex((v) => v.toLowerCase() === lowVal)
      );
    }

    return !!(~excArray.findIndex((v) => v.toLowerCase() === lowVal) && isNew) || !!~incArray.findIndex((v) => v.toLowerCase() === lowVal);
  }

  showHidePlaneTextHandler(state: boolean): void {
    if (this.showPlainText === state) return;

    this.showPlainText = state;

    if (!this.showPlainText) this.getBaseTreeData();
  }

  includeExcludeStringBlur(event, name: string): void {
    if (!event.target.value) this.foldersForm.get(name).reset('');
  }

  includeExcludeInputHandler(event, isInclude = true): void {
    const dataForPath: DataForPath = {
      isUnix: this.isUnix,
      regExpAfter: this.regExpForInputAfter,
      regExpBefore: this.regExpForInputBefore,
      split: this.splitForInput,
      join: this.joinForInput,
      splitForBefore: this.splitForInputBefore
    };
    const formattedObj = BaseForStepsHelper.getStringPathFormatted(event, dataForPath);
    if (formattedObj && formattedObj.needUpdate) {
      this.foldersForm.get(isInclude ? 'includeString' : 'excludeString').patchValue(formattedObj.path);
    }
  }

  getFoldersFromText(value: string): string[] {
    return (value || '')
      .split('\n')
      .map((str) => str.trim())
      .filter(Boolean);
  }

  getFilteredStringsByString(myArray, str: string, isInclude = true): string[] {
    const last = str.slice(-1);
    const lowStr = this.isUnix ? str : str.toLowerCase();

    return Array.from(
      myArray.filter((f) => {
        const lowF = this.isUnix ? f : f.toLowerCase();

        if (isInclude && !lowStr.includes(lowF)) return true;

        if (isInclude) {
          const fLast = f.slice(-1);
          const strByFLen = str[f.length];

          return fLast && fLast !== this.joinForInput && strByFLen && strByFLen !== this.joinForInput;
        }

        if (!lowF.includes(lowStr)) return true;

        const fByStrLen = f[str.length];

        return last && last !== this.joinForInput && fByStrLen && fByStrLen !== this.joinForInput;
      })
    );
  }

  getValidNetworkPath(str: string): string {
    const valuePath = StepsHelpers.getRootPathForShare(str);
    const pathWithEnd = !str.endsWith('\\') ? str + '\\' : str;

    return valuePath && valuePath === pathWithEnd ? pathWithEnd : str;
  }

  addIncludeExcludeStringHandler(event, isInclude = true): void {
    const strControl = this.foldersForm.get(isInclude ? 'includeString' : 'excludeString') as FormControl;

    if (event.id === 'closeIncludeFolderButton' || event.id === 'closeExcludeFolderButton') {
      this.editedString = null;

      return void this.resetFormsAfterChangeStrings(strControl);
    }

    const trimStr = this.getNormalizedPath(strControl.value);
    const newStr = networkValidPath.test(strControl.value) ? this.getValidNetworkPath(trimStr) : trimStr;
    const valid = this.getValidState(strControl);

    if (valid && newStr && (event.id === 'newIncludeFolderButton' || event.id === 'newExcludeFolderButton')) {
      this.changeIncludeExcludeStringsArrays(isInclude, newStr);
      this.resetFormsAfterChangeStrings(strControl);
    }
  }

  resetFormsAfterChangeStrings(strControl): void {
    strControl.reset('');
    this.needValidate = false;
  }

  changeIncludeExcludeStringsArrays(isInclude = true, newStr: string): void {
    const controlName = isInclude ? 'includeFolders' : 'excludeFolders';
    const otherName = !isInclude ? 'includeFolders' : 'excludeFolders';
    const folders = BaseForStepsHelper.getFilteredStringsByString(
      this.stepForm.get(controlName),
      newStr,
      this.editedString && this.editedString.value,
      this.isUnix
    );
    const secondFolders = this.getFilteredStringsByString(this.stepForm.get(otherName).value, newStr, isInclude);

    if (this.editedString) {
      BaseForStepsHelper.updateFoldersAfterEdit(newStr, this.editedString.value, folders, this.isUnix);
      this.stepForm.get(controlName).reset([...folders]);
      this.stepForm.get(otherName).reset([...secondFolders]);
      this.editedString = null;
      return;
    }

    this.stepForm.get(controlName).reset([...folders, newStr]);
    this.stepForm.get(otherName).reset([...secondFolders]);
  }

  someStringExist(strings, inculde, exclude): boolean {
    if (this.isUnix) return strings.some((s) => exclude.some((str) => s === str) || inculde.some((str) => s === str));

    return strings.some((s) => {
      const lowS = s.toLowerCase();
      return exclude.some((str) => lowS === str.toLowerCase()) || inculde.some((str) => lowS === str.toLowerCase());
    });
  }

  pasteFromClipboardHandler(error: TemplateRef<HTMLElement>, isInclude = true): void {
    navigator.clipboard
      .readText()
      .then((value) => {
        const strings = BaseForStepsHelper.stringsArrayFromClipboard(value, this.isUnix, this.splitForInput, this.joinForInput);
        const includeItems = this.stepForm.get(isInclude ? 'includeFolders' : 'excludeFolders').value;
        const otherStrings = this.stepForm.get(!isInclude ? 'includeFolders' : 'excludeFolders').value;
        const exist = this.someStringExist(strings, includeItems, otherStrings);
        const invalidPaths = strings.filter((s) => (this.isUnix ? linuxInvalidPath.test(s) : windowsInvalidPath.test(s)));
        const valid = !invalidPaths.length && !exist;

        if (valid) {
          const filteredOldStrings = BaseForStepsHelper.getFilteredStrings(includeItems, strings, this.joinForInput, this.isUnix);
          filteredOldStrings.push(...strings);

          this.needIncludeStrings = isInclude
            ? BaseForStepsHelper.filterFoldersIfExistParent(filteredOldStrings, this.joinForInput, this.isUnix)
            : otherStrings;
          this.needExcludeStrings = isInclude
            ? otherStrings
            : BaseForStepsHelper.filterFoldersIfExistParent(filteredOldStrings, this.joinForInput, this.isUnix);

          this.updateStrings();
          return void this.resetFoldersFormIfNeed();
        }

        const title = exist
          ? this.i18nPipe.transform('shared:validation.must_unique', { name: 'Path' })
          : this.i18nPipe.transform('shared:validation.invalid', { name: 'path' });
        const body = exist
          ? this.i18nPipe.transform('shared:validation.contains_selected_path')
          : this.i18nPipe.transform('shared:validation.clipboard_invalid_path', { paths: invalidPaths.join(', ') });

        this.stepService.infoModalShow(title, body);
        this.resetFoldersFormIfNeed();
      })
      .catch(() => this.stepService.infoModalShow(this.i18nPipe.transform('shared:validation.read_permission'), error));
  }

  editHandler(index: number, isInclude = true): void {
    const strControl = this.foldersForm.get(isInclude ? 'includeString' : 'excludeString');
    const folders = this.stepForm.get(isInclude ? 'includeFolders' : 'excludeFolders');

    this.editedString = { value: folders.value[index], isInclude, index };
    strControl.setValue(folders.value[index]);
  }

  delIncludeExcludeStringHandler(index: number, isInclude = true): void {
    const control = this.stepForm.get(isInclude ? 'includeFolders' : 'excludeFolders');
    const array = Array.from(control.value);

    array.splice(index, 1);
    control.patchValue(array);
    this.foldersForm.get(isInclude ? 'includeString' : 'excludeString').updateValueAndValidity();
  }

  getSubtree(root: TreeElement): Observable<TreeElement[]> {
    const subtree$ = new BehaviorSubject<TreeElement[]>(null);

    this.treeParams.params.offset = root?.children?.length | 0;
    if (root.indeterminate && !root.checked) (root as MyTreeElement).needUncheckIfChildUncheck = true;

    this.getSubtreeRequest(root, subtree$);

    return subtree$ as Observable<TreeElement[]>;
  }

  getSubtreeRequest(root: MyTreeElement, subtree$: BehaviorSubject<TreeElement[]>): void {
    const pathArrays: PathArrays = this.getPathArraysAndSetParamsPath(root);

    this.stepService
      .getRemoteCommandData(this.treeParams, this.hid)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data) => subtree$.next(this.getTreeItemsAndUpdateRoot(data, root, pathArrays)),
        error: (e) => {
          if (this.networkCredentialsErrors.includes(+e?.error?.errorCode || 0)) {
            return void this.openShareCredentialsModal(root, subtree$);
          }

          subtree$.next([]);
        }
      });
  }

  openShareCredentialsModal(root: MyTreeElement, subtree$: BehaviorSubject<TreeElement[]>): void {
    const settings = new ModalSettings();
    settings.isCustom = true;
    settings.data = { root };

    this.modal
      .openCustom(CredentialsModalComponent, settings)
      .then((result: { login: string; password: string }) => {
        this.treeParams.params.login = result.login;
        this.treeParams.params.password = result.password;
        this.getSubtreeRequest(root, subtree$);
      })
      .catch(() => {
        root.loadingChildren = false;
        this.treeComponent.loadingSubtree = false;
      });
  }

  changeTreeHandler(item: MyTreeElement): void {
    this.changeTreeStateFromItemChanged(item);

    this.updateStringsArraysFromItem(item, this.getStringFromTreeItem(item));
    let parent: MyTreeElement = item.parent || item;
    while (parent?.parent) {
      parent = parent.parent;
    }
    this.updateStringsFromTree(parent);

    this.needUpdate = true;
    if (!this.showPlainText) this.updateStrings();
  }

  updateStrings(): void {
    const mainForm = this.stepForm.get('includeFolders');
    const secondForm = this.stepForm.get('excludeFolders');
    mainForm.reset([...new Set(this.needIncludeStrings)]);
    secondForm.reset([...new Set(this.needExcludeStrings)]);
    this.needUpdate = false;
    this.foldersForm.get('includeString').updateValueAndValidity();
    this.foldersForm.get('excludeString').updateValueAndValidity();
  }

  showLegend(): void {
    const modal = this.modal.openRef(CheckboxLegendComponent, { isCustom: true });

    modal.result.then(noop).catch(noop);
  }

  addNetworkShare(): void {
    const settings = new ModalSettings();
    settings.isCustom = true;
    settings.data = {
      canUseShareTree: this.mainService.backupVersionUpdated && +this.mainService.backupVersionUpdated.substring(0, 3) >= 795, // TODO Check valid version
      networkSharePathArray: Array.from(this.networkSharePathArray),
      hid: this.mainService.hid,
      dataForPath: this.getValueForOperationsWithPath()
    };

    this.modal
      .openCustom(NetworkShareModalComponent, settings)
      .then((newPathArray: NetworkCredentials[]) => {
        this.networkSharePathArray = newPathArray;

        const shareHash: { [key: string]: TreeElement } = {};
        const newTreeData = this.treeComponent.data.filter(
          (el: MyTreeElement) => !el.isShare || newPathArray.some((cred) => cred.id === el.id)
        );
        newTreeData.forEach((item) => (item as MyTreeElement).isShare && (shareHash[item.id] = item));
        this.shareTreeItems = [];
        this.needIncludeStrings = this.stepForm.get('includeFolders').value;
        this.needExcludeStrings = this.stepForm.get('excludeFolders').value;

        newPathArray.forEach((item: NetworkCredentials) => {
          const path = (shareHash[item.id] as MyTreeElement)?.path?.toLowerCase();
          const isEqualPath = path === item.path.toLowerCase();

          if (!isEqualPath) {
            this.needIncludeStrings = this.needIncludeStrings.filter((str: string) => {
              return StepsHelpers.pathNotIncludePath(false, this.joinForInput, str, item.path);
            });
            this.needExcludeStrings = this.needExcludeStrings.filter((str: string) => {
              return StepsHelpers.pathNotIncludePath(false, this.joinForInput, str, item.path);
            });

            this.shareTreeItems.push({
              label: item.path,
              id: item.id,
              checked: false,
              indeterminate: false,
              icon: this.shareIconPath,
              gotChildren: false,
              shown: true,
              path: item.path,
              isShare: true
            } as MyTreeElement);
          }
        });

        newTreeData.push(...this.shareTreeItems);
        this.setTreeData(newTreeData);
        this.stepForm.get('includeFolders').reset(this.needIncludeStrings);
        this.stepForm.get('excludeFolders').reset(this.needExcludeStrings);
      })
      .catch(noop);
  }

  private setTreeData(data: typeof this.treeData): void {
    if ((this.isFileBackup || this.view === this.viewMode.Onboarding) && !this.isUnix) {
      data.forEach((item) => {
        if (!item.parent && item.path === 'C:\\') {
          item.canShowIBBAlert = true;
        }
      });
    }

    this.treeData = [...data];
  }

  // Private block start
  private resetFoldersFormIfNeed(): void {
    const formInput = this.foldersForm.get('includeString');

    if (!formInput.value) formInput.reset('');
  }

  private getNormalizedPath(path: string): string {
    let trimPath = path.trim();

    if (!this.isUnix) {
      trimPath = this.getUnixNormalizedPath(trimPath);
    } else if (trimPath !== '/' && trimPath !== '//') {
      trimPath = trimPath.endsWith('/') ? (trimPath.endsWith('//') ? trimPath.slice(0, -2) : trimPath.slice(0, -1)) : trimPath;
    }

    return trimPath;
  }

  private getUnixNormalizedPath(trimPath: string): string {
    return trimPath.endsWith(this.joinForInput)
      ? trimPath.endsWith(this.joinForInput + this.joinForInput)
        ? trimPath.slice(0, -2)
        : /^([a-zA-Z]:)/.test(trimPath)
          ? trimPath
          : trimPath.slice(0, -1)
      : trimPath;
  }

  private getValidState(strControl: FormControl<string>): boolean {
    const errorsCount = !strControl.valid && Object.keys(strControl.errors).length;

    return (
      strControl.valid ||
      (errorsCount === 1
        ? strControl.errors.whitespace || strControl.errors.required
        : errorsCount === 2
          ? strControl.errors.whitespace && strControl.errors.required
          : false)
    );
  }

  private getBaseTreeData(): void {
    this.treeParams.params.path = '';
    this.needIncludeStrings = this.stepForm.get('includeFolders').value;
    this.needExcludeStrings = this.stepForm.get('excludeFolders').value;
    this.loading = true;

    this.stepService
      .getRemoteCommandData(this.treeParams, this.hid)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data) => {
          if (data?.data) {
            this.setTreeData(
              data.data.items.map((item, idx) => {
                return {
                  label: item.displayName,
                  id: idx.toString(),
                  checked: false,
                  expanded: false,
                  icon: +item.type === 1 ? TreeIconPath.Folder : TreeIconPath.Disk,
                  shown: true,
                  path: item.path
                };
              })
            );
            this.updateTreeFromStepFormArray();
            this.loading = false;
          }
        },
        error: () => (this.loading = false)
      });
  }

  private getPathArraysAndSetParamsPath(root: TreeElement): PathArrays {
    const mainFormArray = this.stepForm.get('includeFolders').value;
    const excFormArray = this.stepForm.get('excludeFolders').value;
    let newInc = [];
    let newExc = [];

    this.treeParams.params.path =
      this.isUnix || networkValidPath.test((root as MyTreeElement).path)
        ? (root as MyTreeElement).path
        : StepsHelpers.getPathInStringFormatByOs(root, this.isUnix);

    if (this.isUnix) {
      newInc = StepsHelpers.findInNotRootFolder(mainFormArray, this.treeData);
      newExc = StepsHelpers.findInNotRootFolder(excFormArray, this.treeData);
    }

    return { mainFormArray, excFormArray, newInc, newExc };
  }

  private updateDataItemsAndGetSettingsForHelper(
    data: ComputerContentRequestData,
    root: TreeElement,
    newInc: string[],
    newExc: string[]
  ): GenerateTreeDataSettings {
    const existData = data?.data?.items?.length;
    const settings = {
      root,
      newInc,
      newExc,
      joinStr: this.joinForInput,
      sep: this.separator,
      alt: existData && isNaN(+data.data.items[0].type),
      isUnix: this.isUnix
    };

    if (existData) {
      data.data.items = StepsHelpers.getSortedTreeItems(data.data.items, settings.alt);
    }

    return settings;
  }

  private getTreeItemsAndUpdateRoot(data: ComputerContentRequestData, root: MyTreeElement, pathArrays: PathArrays): TreeElement[] {
    const newElements: ExtendedTreeElement[] = StepsHelpers.generateTreeData(
      data,
      pathArrays.mainFormArray,
      pathArrays.excFormArray,
      this.updateDataItemsAndGetSettingsForHelper(data, root, pathArrays.newInc, pathArrays.newExc)
    );

    root.totalChildren = StepsHelpers.getTotalChildren(data?.data, newElements.length);

    return newElements;
  }

  private floatNeedUncheckToLast(item: MyTreeElement): void {
    item.needUncheckIfChildUncheck = !item.notSetUncheckFlag;
    if (item.parent && !item.parent.checked) this.floatNeedUncheckToLast(item.parent);
  }

  private floatNotNeedUncheckToLast(item: MyTreeElement): void {
    if (item?.needUncheckIfChildUncheck) item.checked = false;

    if (
      item?.needUncheckIfChildUncheck &&
      item.children?.length &&
      !item.children[0].indeterminate &&
      (item.children.length === 1 ||
        item.children.every((c) => c.indeterminate === item.children[0].indeterminate && c.checked === item.children[0].checked))
    ) {
      item.indeterminate = false;
    }

    if (
      !item?.needUncheckIfChildUncheck &&
      item?.children?.length > 1 &&
      item.children[0].checked &&
      item.children.every((c) => !c.indeterminate && c.checked === item.children[0].checked)
    ) {
      item.indeterminate = false;
    }

    if (item?.parent) this.floatNotNeedUncheckToLast(item.parent);
  }

  private floatNeedUncheckToLastIfChecked(item: MyTreeElement): void {
    if (item?.children?.every((c) => c.checked)) {
      item.checked = true;
      item.indeterminate = false;
      item.needUncheckIfChildUncheck = !item.notSetUncheckFlag;

      this.floatNeedUncheckToLastIfChecked(item.parent);
    }
  }

  private changeTreeStateFromItemChanged(item: MyTreeElement): void {
    item.notSetUncheckFlag = item.checked;
    this.updateChildrenNotSetUncheckFlag(item.children, item.checked);
    item.needUncheckIfChildUncheck = false;

    if (!item.checked || !item.parent) {
      return void (item.parent && this.floatNotNeedUncheckToLast(item.parent));
    }

    if (!item.parent.checked) return void this.floatNeedUncheckToLast(item.parent);

    if (!(item.parent as MyTreeElement).notSetUncheckFlag && item.parent.children?.length === 1 && item.parent.children[0].checked) {
      return void this.floatNeedUncheckToLastIfChecked(item.parent);
    }

    this.checkParentToAllCheckedAndUpdateCheckState(item.parent);
  }

  // Agents from 4.6 and the .NET build expand %Variable% in a unix path the way the Windows agent always has
  private get pathValidator(): (control: AbstractControl) => ValidationErrors | null {
    if (!this.isUnix) return winNetworkPathValidator;

    const canUseVariables = this.isNetBuild || this.agentHigherOrEqualThat(AgentVersions.UnixPathVariables);

    return canUseVariables ? unixWithVariablesPathValidator : linuxPathValidator;
  }

  private getPathsErrors(control: AbstractControl, isInclude: boolean): Record<number, ValidationErrors> {
    const pathValidator = this.pathValidator;
    const otherPaths = this.getFoldersFromText(control?.parent?.get(isInclude ? 'excludePaths' : 'includePaths')?.value);
    const lines = ((control?.value as string) || '').split('\n');

    return lines.reduce((errors, line, index) => {
      const path = line.trim();

      if (!path) return errors;

      const pathErrors = pathValidator({ value: path } as AbstractControl) || this.getSelectedPathError(path, index, lines, otherPaths);

      return pathErrors ? { ...errors, [index]: pathErrors } : errors;
    }, {});
  }

  private getSelectedPathError(path: string, index: number, lines: string[], otherPaths: string[]): ValidationErrors | null {
    const exist =
      lines.some((line, lineIndex) => lineIndex !== index && this.isSamePath(line.trim(), path)) ||
      otherPaths.some((otherPath) => this.isSamePath(otherPath, path));

    return exist ? { isSelected: { message: this.i18nPipe.transform(ExistingFolderI18Key) } } : null;
  }

  private isSamePath(first: string, second: string): boolean {
    return this.isUnix ? first === second : first?.toLowerCase() === second?.toLowerCase();
  }

  private revalidatePathsControls(): void {
    this.foldersForm.get('includePaths').updateValueAndValidity({ emitEvent: false });
    this.foldersForm.get('excludePaths').updateValueAndValidity({ emitEvent: false });

    if (!this.showPlainText) return;

    this.foldersForm.get('includeString').updateValueAndValidity({ emitEvent: false });
    this.foldersForm.get('excludeString').updateValueAndValidity({ emitEvent: false });
  }

  private syncFoldersTextIfNeed(value: WhatBackupTreeStepValue): void {
    const includeSynced = this.syncFolderTextControl('includePaths', value?.includeFolders);
    const excludeSynced = this.syncFolderTextControl('excludePaths', value?.excludeFolders);

    if (includeSynced || excludeSynced) this.revalidatePathsControls();
  }

  private syncFolderTextControl(name: string, folders: string[] = []): boolean {
    const control = this.foldersForm?.get(name);

    if (!control || isEqual(this.getFoldersFromText(control.value), folders || [])) return false;

    control.setValue((folders || []).join('\n'), { emitEvent: false });

    return true;
  }

  private checkParentToAllCheckedAndUpdateCheckState(item: MyTreeElement): void {
    if (!item?.parent) return;

    if (!item.parent.checked && item.parent.children?.every((c) => c.checked && !c.indeterminate)) {
      item.parent.checked = true;
      item.parent.indeterminate = false;
    }

    this.checkParentToAllCheckedAndUpdateCheckState(item.parent);
  }

  private updateChildrenNotSetUncheckFlag(children: MyTreeElement[], state: boolean): void {
    if (children?.length) {
      children?.forEach((c) => {
        c.notSetUncheckFlag = state;
        this.updateChildrenNotSetUncheckFlag(c.children, state);
      });
    }
  }

  private getLastNeedUncheckParent(item: MyTreeElement): MyTreeElement {
    let needNextParent = (item.parent as MyTreeElement)?.needUncheckIfChildUncheck && (item.parent?.checked || !item.parent?.indeterminate);
    let parent: MyTreeElement = needNextParent ? item.parent : null;

    while (needNextParent) {
      needNextParent =
        (parent.parent as MyTreeElement)?.needUncheckIfChildUncheck && (parent.parent?.checked || !parent.parent?.indeterminate);

      if (needNextParent) parent = parent.parent;
    }

    return parent;
  }

  private updateStringsArraysFromItem(item: MyTreeElement, str = ''): void {
    const parentForGetStr = this.getLastNeedUncheckParent(item);
    const newStr = parentForGetStr ? this.getStringFromTreeItem(parentForGetStr) : str;

    this.filterNeedUpdatedStringsArray(newStr, this.needIncludeStrings);
    this.filterNeedUpdatedStringsArray(newStr, this.needExcludeStrings, false);
    this.needIncludeStrings = this.needIncludeStrings.filter((s) => !StepsHelpers.equalStrings(s, newStr, this.isUnix));
    this.needExcludeStrings = this.needExcludeStrings.filter((s) => !StepsHelpers.equalStrings(s, newStr, this.isUnix));
    this.deleteStringsByParent(item.parent);
  }

  private deleteStringsByParent(item: MyTreeElement): void {
    let parent: MyTreeElement = item;

    while (parent) {
      const str = this.getStringFromTreeItem(parent);

      if (parent.checked && !parent.indeterminate) {
        this.filterNeedUpdatedStringsArray(str, this.needIncludeStrings);
        this.filterNeedUpdatedStringsArray(str, this.needExcludeStrings, false);
      }

      if (!parent.checked && parent.indeterminate) {
        this.needIncludeStrings = this.needIncludeStrings.filter((s) => !StepsHelpers.equalStrings(s, str, this.isUnix));
        this.needExcludeStrings = this.needExcludeStrings.filter((s) => !StepsHelpers.equalStrings(s, str, this.isUnix));
      }

      parent = parent.parent;
    }
  }

  private updateStringsFromTree(item: MyTreeElement, path = '', needPushChecked = true): void {
    const pathArrayName = needPushChecked ? 'needIncludeStrings' : 'needExcludeStrings';

    if (item.checked ? needPushChecked : !needPushChecked) {
      const str = path || this.getStringFromTreeItem(item);
      this[pathArrayName].push(str);
    }

    if (item.indeterminate && item.children?.length) item.children.forEach((child) => this.updateStringsFromTree(child, '', !item.checked));
  }

  private filterNeedUpdatedStringsArray(str: string, stringsArray: string[], isInclude = true): void {
    const strArrayLength = str.split(this.joinForInput).filter((s) => !!s && s !== ' ').length;
    const lowStr = this.isUnix ? str : str.toLowerCase();
    const newArray = stringsArray.filter((s) => {
      const lowS = this.isUnix ? s : s.toLowerCase();
      const sLen = s.split(this.joinForInput).filter((s) => !!s && s !== ' ').length;
      return strArrayLength === sLen
        ? lowS !== lowStr
        : !lowS.startsWith(lowStr) ||
            (StepsHelpers.pathNotIncludePath(this.isUnix, this.joinForInput, lowS, lowStr) &&
              (StepsHelpers.pathNotIncludePath(this.isUnix, this.joinForInput, lowStr, lowS) || strArrayLength > sLen));
    });

    this[isInclude ? 'needIncludeStrings' : 'needExcludeStrings'] = newArray;
  }

  private getStringFromTreeItem(item: TreeElement & { path?: string }): string {
    return item.path;
  }

  private getItemPath(item: TreeElement): string {
    return this.isUnix ? (item as MyTreeElement).path : (item.label as string);
  }

  private updateTreeFromStepFormArray(): void {
    const incArray = this.stepForm.get('includeFolders').value;
    const excArray = this.stepForm.get('excludeFolders').value;
    const incExistInTree: string[] = [];
    const excExistInTree: string[] = [];

    this.treeData.forEach((item: TreeElement) => {
      const path = this.getItemPath(item);
      const incCurrent = incArray.some((incStr) => !StepsHelpers.pathNotIncludePath(this.isUnix, this.joinForInput, incStr, path));
      const excCurrent = excArray.some((excStr) => !StepsHelpers.pathNotIncludePath(this.isUnix, this.joinForInput, excStr, path));
      item.checked = incArray.some((s) => StepsHelpers.equalStrings(path, s, this.isUnix));

      if (this.isUnix && path === this.joinForInput) {
        const newInc = StepsHelpers.findInNotRootFolder(incArray, this.treeData);
        const newExc = StepsHelpers.findInNotRootFolder(excArray, this.treeData);
        item.indeterminate = !!newInc.length && (!item.checked || !!newExc.length);
      } else {
        item.indeterminate = incCurrent && (!item.checked || excCurrent);
      }

      if (incCurrent || item.checked) incExistInTree.push(path);
      if (excCurrent) excExistInTree.push(path);
    });

    if (!this.isUnix) {
      this.addShareTreeItems(incArray, excArray);
      this.addTreeItemFroUniquePaths(incArray, excArray, incExistInTree, excExistInTree);
    }
  }

  private addShareTreeItems(incArray: string[], excArray: string[]): void {
    this.shareTreeItems = [];

    const newInc = incArray.filter((str: string) => networkValidPath.test(str));
    const newExc = excArray.filter((str: string) => networkValidPath.test(str));
    const rootsMap: { [key: string]: boolean } = {};

    newInc.forEach((path: string, idx: number) => {
      const rootPath = StepsHelpers.getRootPathForShare(path);
      const checked = newInc.some((s) => StepsHelpers.equalStrings(rootPath, s, this.isUnix));
      const existInExclude = newExc.some((str) => !StepsHelpers.pathNotIncludePath(this.isUnix, this.joinForInput, str, path));

      if (!rootsMap[rootPath]) {
        rootsMap[rootPath] = true;

        this.shareTreeItems.push({
          label: rootPath,
          id: idx - 1 + this.separator + rootPath.replaceAll(' ', '-'),
          checked: checked,
          indeterminate: !checked || existInExclude,
          icon: this.shareIconPath,
          gotChildren: false,
          shown: true,
          path: rootPath,
          isShare: true
        } as MyTreeElement);
      }
    });

    this.networkSharePathArray = this.shareTreeItems.map((item) => ({ path: (item as MyTreeElement).path, login: '', id: item.id }));
    this.treeData.push(...this.shareTreeItems);
  }

  private addTreeItemFroUniquePaths(incArray: string[], excArray: string[], incExistInTree: string[], excExistInTree: string[]): void {
    const uniqIncArray = incArray.filter((str: string) => /^(%)([A-Za-z0-9 _\-.]*%\\)/.test(str) && !incExistInTree.includes(str));
    const uniqExcArray = excArray.filter((str: string) => /^(%)([A-Za-z0-9 _\-.]*%\\)/.test(str) && !excExistInTree.includes(str));

    uniqIncArray.forEach((path: string, idx: number) => {
      const existInExclude = uniqExcArray.some((str) => !StepsHelpers.pathNotIncludePath(this.isUnix, this.joinForInput, str, path));
      this.treeData.push({
        label: path,
        id: idx + this.separator + path.replaceAll(' ', '-'),
        checked: !existInExclude,
        indeterminate: existInExclude,
        icon: TreeIconPath.Folder,
        gotChildren: true,
        shown: true,
        path: path
      } as TreeElement);
    });
  }
}
